import { useTranslation } from 'react-i18next';
import { STATES, getCitiesForState } from '../../../data/locations';
import { LinesSkeleton } from '../../UI/Skeleton';

function CameraIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
      <path d="M12 15.2a3.2 3.2 0 0 100-6.4 3.2 0 000 6.4 3.2 0 000 6.4z" />
      <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5 5 2.24 5 5-2.24 5 5-2.24 5 5-5z" />
    </svg>
  );
}

export default function EditProfileForm({
  user,
  loaded,
  saving,
  uploading,
  onSubmit,
  // Form state
  name,
  setName,
  phone,
  setPhone,
  about,
  setAbout,
  avatar,
  setAvatar,
  profileState,
  setProfileState,
  profileCity,
  setProfileCity,
  companyName,
  setCompanyName,
  languages,
  setLanguages,
  gender,
  setGender,
  website,
  setWebsite,
  coverPhoto,
  setCoverPhoto,
  alternatePhone,
  setAlternatePhone,
  preferredLanguage,
  setPreferredLanguage,
  businessInfo,
  setBusinessInfo,
  socialLinks,
  setSocialLinks,
  privacySettings,
  setPrivacySettings,
  // Avatar upload
  fileInputRef,
  avatarPreview,
  handleAvatarSelect,
  removeAvatar,
  uploading: avatarUploading,
}) {
  const { t } = useTranslation();
  const initial = (name || user?.displayName || 'U').charAt(0).toUpperCase();
  const displayAvatar = avatarPreview || avatar;

  if (!loaded) return <LinesSkeleton count={4} />;

  return (
    <form onSubmit={onSubmit} className="epForm">
      <div className="epAvatarSection">
        <button
          type="button"
          className="epAvatarWrap"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          aria-label={t('profile.changeProfilePicture')}
        >
          {displayAvatar ? (
            <img src={displayAvatar} alt={t('profile.profile')} className="epAvatarImg" />
          ) : (
            <span className="epAvatarPlaceholder">{initial}</span>
          )}
          <span className="epAvatarOverlay">
            <CameraIcon />
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleAvatarSelect}
          className="epHiddenInput"
        />
        <div className="epAvatarActions">
          <button
            type="button"
            className="epBtn epBtnPrimary epBtnSmall epBtnRound"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {displayAvatar ? t('profile.changePhoto') : t('profile.uploadPhoto')}
          </button>
          {displayAvatar && (
            <button
              type="button"
              className="epBtn epBtnDanger epBtnSmall epBtnRound"
              onClick={() => {
                removeAvatar();
                setAvatar(null);
              }}
            >
              {t('common.remove')}
            </button>
          )}
        </div>
        {avatarUploading && <p className="epHintAccent">{t('profile.uploading')}</p>}
        <p className="epHintMuted">{t('profile.maxSize')}</p>
      </div>

      <div className="epField">
        <label htmlFor="edit-profile-name" className="epLabel">
          {t('profile.name')}
        </label>
        <input
          id="edit-profile-name"
          type="text"
          className="epInput"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('profile.yourName')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-phone" className="epLabel">
          {t('profile.phone')}
        </label>
        <input
          id="edit-profile-phone"
          type="tel"
          className="epInput"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t('profile.phoneNumber')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-about" className="epLabel">
          {t('profile.about')}
        </label>
        <textarea
          id="edit-profile-about"
          className="epInput epTextarea"
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          placeholder={t('profile.aboutPlaceholder')}
          rows={3}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-gender" className="epLabel">
          {t('profile.gender')} <span className="epLabelOptional">({t('common.optional')})</span>
        </label>
        <select
          id="edit-profile-gender"
          className="epInput"
          value={gender}
          onChange={(e) => setGender(e.target.value)}
        >
          <option value="">{t('profile.preferNotToSay')}</option>
          <option value="Male">{t('profile.male')}</option>
          <option value="Female">{t('profile.female')}</option>
          <option value="Other">{t('profile.other')}</option>
        </select>
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-state" className="epLabel">
          {t('profile.location')}{' '}
          <span className="epLabelOptional">({t('profile.visibleOnProfile')})</span>
        </label>
        <div className="epLocationRow">
          <select
            id="edit-profile-state"
            className="epInput"
            value={profileState}
            onChange={(e) => {
              setProfileState(e.target.value);
              setProfileCity('');
            }}
          >
            <option value="">{t('profile.selectState')}</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            id="edit-profile-city"
            className="epInput"
            value={profileCity}
            onChange={(e) => setProfileCity(e.target.value)}
            disabled={!profileState}
          >
            <option value="">{t('profile.selectCity')}</option>
            {getCitiesForState(profileState).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-languages" className="epLabel">
          {t('profile.languages')}{' '}
          <span className="epLabelOptional">({t('profile.helpsBuyersConnect')})</span>
        </label>
        <input
          id="edit-profile-languages"
          type="text"
          className="epInput"
          value={languages}
          onChange={(e) => setLanguages(e.target.value)}
          placeholder={t('profile.languagesPlaceholder')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-company" className="epLabel">
          {t('profile.companyName')}{' '}
          <span className="epLabelOptional">({t('common.optional')})</span>
        </label>
        <input
          id="edit-profile-company"
          type="text"
          className="epInput"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder={t('profile.companyNamePlaceholder')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-website" className="epLabel">
          {t('profile.website')}{' '}
          <span className="epLabelOptional">({t('common.optional')})</span>
        </label>
        <input
          id="edit-profile-website"
          type="url"
          className="epInput"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder={t('profile.websitePlaceholder')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-coverPhoto" className="epLabel">
          {t('profile.coverPhoto')}
        </label>
        <input
          id="edit-profile-coverPhoto"
          type="url"
          className="epInput"
          value={coverPhoto || ''}
          onChange={(e) => setCoverPhoto(e.target.value || null)}
          placeholder={t('profile.websitePlaceholder')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-alternatePhone" className="epLabel">
          {t('profile.alternatePhone')}
        </label>
        <input
          id="edit-profile-alternatePhone"
          type="tel"
          className="epInput"
          value={alternatePhone}
          onChange={(e) => setAlternatePhone(e.target.value)}
          placeholder={t('common.optional')}
        />
      </div>
      <div className="epField">
        <label htmlFor="edit-profile-preferredLanguage" className="epLabel">
          {t('profile.preferredLanguage')}
        </label>
        <select
          id="edit-profile-preferredLanguage"
          className="epInput"
          value={preferredLanguage}
          onChange={(e) => setPreferredLanguage(e.target.value)}
        >
          <option value="en">{t('profile.english')}</option>
          <option value="fr">{t('profile.french')}</option>
          <option value="it">{t('profile.italian')}</option>
          <option value="de">{t('profile.german')}</option>
          <option value="es">{t('profile.spanish')}</option>
          <option value="bg">{t('profile.bulgarian')}</option>
        </select>
      </div>

      <h3 className="epSectionTitle">{t('profile.businessInfo')}</h3>
      <div className="epField">
        <label className="epLabel">{t('profile.type')}</label>
        <select
          className="epInput"
          value={businessInfo.type}
          onChange={(e) =>
            setBusinessInfo((p) => ({ ...p, type: e.target.value }))
          }
        >
          <option value="individual">{t('profile.individual')}</option>
          <option value="dealer">{t('profile.dealer')}</option>
          <option value="business">{t('profile.business')}</option>
        </select>
      </div>
      <div className="epField">
        <label className="epLabel">{t('profile.vatNumber')}</label>
        <input
          type="text"
          className="epInput"
          value={businessInfo.vatNumber}
          onChange={(e) =>
            setBusinessInfo((p) => ({ ...p, vatNumber: e.target.value }))
          }
          placeholder={t('common.optional')}
        />
      </div>
      <div className="epField">
        <label className="epLabel">{t('profile.shopAddress')}</label>
        <input
          type="text"
          className="epInput"
          value={businessInfo.shopAddress}
          onChange={(e) =>
            setBusinessInfo((p) => ({ ...p, shopAddress: e.target.value }))
          }
          placeholder={t('common.optional')}
        />
      </div>
      <div className="epField">
        <label className="epLabel">{t('profile.businessHours')}</label>
        <input
          type="text"
          className="epInput"
          value={businessInfo.businessHours}
          onChange={(e) =>
            setBusinessInfo((p) => ({ ...p, businessHours: e.target.value }))
          }
          placeholder={t('profile.businessHoursPlaceholder')}
        />
      </div>

      <h3 className="epSectionTitle">{t('profile.socialLinks')}</h3>
      {['facebook', 'instagram', 'twitter', 'linkedin', 'youtube'].map(
        (key) => (
          <div key={key} className="epField">
            <label className="epLabel">
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </label>
            <input
              type="url"
              className="epInput"
              value={socialLinks[key] || ''}
              onChange={(e) =>
                setSocialLinks((p) => ({ ...p, [key]: e.target.value }))
              }
              placeholder={`https://${key}.com/...`}
            />
          </div>
        )
      )}

      <h3 className="epSectionTitle">{t('profile.privacySettings')}</h3>
      <div className="epCheckboxRow">
        <input
          id="ep-privacy-showPhone"
          type="checkbox"
          checked={privacySettings.showPhone}
          onChange={(e) =>
            setPrivacySettings((p) => ({ ...p, showPhone: e.target.checked }))
          }
        />
        <label htmlFor="ep-privacy-showPhone">{t('profile.showPhoneToBuyers')}</label>
      </div>
      <div className="epCheckboxRow">
        <input
          id="ep-privacy-showEmail"
          type="checkbox"
          checked={privacySettings.showEmail}
          onChange={(e) =>
            setPrivacySettings((p) => ({ ...p, showEmail: e.target.checked }))
          }
        />
        <label htmlFor="ep-privacy-showEmail">{t('profile.showEmailToBuyers')}</label>
      </div>
      <div className="epCheckboxRow">
        <input
          id="ep-privacy-showLocation"
          type="checkbox"
          checked={privacySettings.showLocation}
          onChange={(e) =>
            setPrivacySettings((p) => ({
              ...p,
              showLocation: e.target.checked,
            }))
          }
        />
        <label htmlFor="ep-privacy-showLocation">{t('profile.showLocation')}</label>
      </div>
      <div className="epField">
        <label className="epLabel">{t('profile.whoCanMessage')}</label>
        <select
          className="epInput"
          value={privacySettings.allowMessages}
          onChange={(e) =>
            setPrivacySettings((p) => ({ ...p, allowMessages: e.target.value }))
          }
        >
          <option value="everyone">{t('profile.everyone')}</option>
          <option value="verified_only">{t('profile.verifiedOnly')}</option>
          <option value="nobody">{t('profile.nobody')}</option>
        </select>
      </div>

      <button
        type="submit"
        className="epBtn epBtnPrimary epBtnLarge"
        disabled={saving || avatarUploading}
      >
        {saving ? t('profile.saving') : t('profile.saveChanges')}
      </button>
    </form>
  );
}
